<template>
    <a-popup :visible="visible" @cancel="onCancel">
        <div
            class="tw-mx-auto tw-w-[95%] md:tw-max-w-[700px] tw-overflow-hidden tw-h-fit tw-bg-white tw-border tw-border-solid tw-border-gray-300 tw-shadow-md tw-my-5 tw-rounded">
            <div class="tw-p-4 tw-text-lg tw-border-b tw-border-solid tw-font-medium tw-text-gray-600 tw-bg-gray-50">
                {{ isEdit ? $t('portal.products.edit') : $t('portal.products.create') }}
            </div>

            <div class="tw-p-5 tw-bg-white">
                <div class="tw-grid tw-grid-cols-12 tw-gap-x-4">
                    <div class="tw-col-span-12 md:tw-col-span-8">
                        <FormField left-icon="solar:box-bold-duotone" v-model="form.name" :label="$t('fields.name')"
                            type="text" :error="err('name')" />
                    </div>
                    <div class="tw-col-span-12 md:tw-col-span-4">
                        <FormField v-model="form.sku" :label="$t('fields.product-sku')" type="text" :error="err('sku')" />
                    </div>
                    <div class="tw-col-span-12 md:tw-col-span-4">
                        <FormField v-model="form.type" :label="$t('portal.fields.type')" type="select"
                            :options="typeOptions" :error="err('type')" />
                    </div>
                    <div class="tw-col-span-12 md:tw-col-span-4">
                        <FormField v-model="form.category_id" :label="$t('portal.fields.category')" type="select"
                            :options="categoryOptions" :error="err('category_id')" />
                    </div>
                    <div class="tw-col-span-12 md:tw-col-span-4">
                        <FormField v-model="form.status" :label="$t('fields.status')" type="select"
                            :options="statusOptions" :error="err('status')" />
                    </div>
                    <div class="tw-col-span-12 md:tw-col-span-6">
                        <FormField v-model="form.selling_price" :label="$t('fields.selling-price')" type="number"
                            :error="err('selling_price')" />
                    </div>
                    <div class="tw-col-span-12 md:tw-col-span-6">
                        <FormField v-model="form.cost" :label="$t('fields.buying-price')" type="number"
                            :error="err('cost')" />
                    </div>
                    <div class="tw-col-span-12">
                        <FormField v-model="form.description" :label="$t('fields.description')" type="textarea"
                            :error="err('description')" />
                    </div>
                </div>
            </div>

            <div class="tw-p-4 tw-border-t tw-justify-end tw-flex tw-items-center tw-gap-2 tw-bg-gray-50">
                <a-button :disabled="loading" @click="onCancel" intent="simple" class="tw-min-w-[120px]">{{ $t('buttons.cancel') }}</a-button>
                <a-button :disabled="loading" :loading="loading" @click="onSave" class="tw-min-w-[120px]">{{ isEdit ? $t('buttons.update') : $t('buttons.create') }}</a-button>
            </div>
        </div>
    </a-popup>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField';
import { create, update } from '@/api/products';
import { all as allCategories } from '@/api/categories';
import useAlert from '@/composables/useAlert';
import { useValidation } from '@/composables/useValidation';

const props = defineProps({
    visible: { type: Boolean, default: false },
    product: { type: Object, default: null },
});
const emit = defineEmits(['cancel', 'saved']);
const i18n = useI18n();

const loading = ref(false);
const errors = ref({});
const form = ref(defaults());
const categories = ref([]);
const err = useValidation(errors);

const isEdit = computed(() => !!props.product?.id);

const typeOptions = [
    { value: 'product', label: i18n.t('portal.types.product') },
    { value: 'service', label: i18n.t('portal.types.service') },
    { value: 'subscription', label: i18n.t('portal.types.subscription') },
];
const statusOptions = [
    { value: 'active', label: i18n.t('common.active') },
    { value: 'inactive', label: i18n.t('common.inactive') },
];
const categoryOptions = computed(() => [
    { value: '', label: i18n.t('common.none') },
    ...categories.value.map((c) => ({ value: c.id, label: c.name })),
]);

function defaults() {
    return { name: '', sku: '', type: 'product', category_id: '', selling_price: '', cost: '', description: '', status: 'active' };
}

async function loadCategories() {
    try {
        const { data } = await allCategories({ status: 'active' });
        categories.value = data.categories || [];
    } catch (e) { /* interceptor */ }
}

watch(() => props.visible, (v) => {
    if (v) {
        errors.value = {};
        form.value = props.product ? { ...defaults(), ...props.product, category_id: props.product.category_id ?? '' } : defaults();
        if (!categories.value.length) loadCategories();
    }
});

const onCancel = () => {
    if (!loading.value) emit('cancel');
};

const onSave = async () => {
    loading.value = true;
    errors.value = {};
    try {
        // Empty category select -> send null so "nullable|exists" passes.
        const payload = { ...form.value, category_id: form.value.category_id || null };
        const req = isEdit.value ? update(props.product.id, payload) : create(payload);
        const { data } = await req;
        if (data.code === 'SUCCESS') {
            useAlert(i18n.t(isEdit.value ? 'messages.updated-successfully' : 'messages.created-successfully', { field: i18n.t('portal.entities.product') }));
            emit('saved', data.product);
        }
    } catch (e) {
        if (e.response?.status === 422) {
            for (const k in e.response.data.errors) errors.value[k] = e.response.data.errors[k][0];
        }
    } finally {
        loading.value = false;
    }
};
</script>
